import React, { useEffect, useState, useRef, useCallback } from 'react';
import { collection, query, where, getDocs, limit, startAfter, doc, getDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../firebase/config';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function Feed() {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('All');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Advanced Filters
  const [sortOrder, setSortOrder] = useState('newest');
  const [conditionFilter, setConditionFilter] = useState('All');
  const [maxPrice, setMaxPrice] = useState('');
  
  const [searchLocation, setSearchLocation] = useState('');
  const [localPickupOnly, setLocalPickupOnly] = useState(false);
  
  // Pagination & Infinite Scroll
  const [lastVisible, setLastVisible] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef();

  const [userFavorites, setUserFavorites] = useState([]);
  const [globalAlert, setGlobalAlert] = useState(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const q = query(collection(db, 'listings'), where('status', '==', 'active'), limit(12));
        const querySnapshot = await getDocs(q);
        const items = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        setListings(items);
        setFilteredListings(items);
        setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
        if (querySnapshot.docs.length < 12) setHasMore(false);

        const catSnap = await getDocs(collection(db, "categories"));
        const adminCategories = catSnap.docs.map(doc => doc.id).sort();
        
        if (adminCategories.length > 0) {
          setAvailableCategories(adminCategories);
        } else {
          const legacyCatQuery = query(collection(db, 'listings'), where('status', '==', 'active'));
          const legacyCatSnap = await getDocs(legacyCatQuery);
          const uniqueCategories = [...new Set(legacyCatSnap.docs.map(doc => doc.data().category).filter(Boolean))];
          setAvailableCategories(uniqueCategories.sort());
        }
      } catch (error) { 
        console.error("Error fetching listings:", error); 
      } finally { 
        setLoading(false); 
      }
    };

    fetchInitialData();

    const alertUnsubscribe = onSnapshot(doc(db, "system", "global_alert"), (docSnap) => {
      if (docSnap.exists() && docSnap.data().active) {
        setGlobalAlert(docSnap.data());
      } else {
        setGlobalAlert(null);
      }
    });

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists() && userDoc.data().favorites) {
          setUserFavorites(userDoc.data().favorites);
        }
      } else {
        setUserFavorites([]);
      }
    });

    return () => {
      unsubscribeAuth();
      alertUnsubscribe(); 
    };
  }, []);

  // --- TRUE INFINITE SCROLL: Intersection Observer ---
  const loadMore = useCallback(async () => {
    if (!lastVisible || !hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const q = query(collection(db, 'listings'), where('status', '==', 'active'), startAfter(lastVisible), limit(12));
      const querySnapshot = await getDocs(q);
      const newItems = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setListings(prev => [...prev, ...newItems]);
      setFilteredListings(prev => [...prev, ...newItems]);
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      if (querySnapshot.docs.length < 12) setHasMore(false);
    } catch (error) {
      console.error("Error loading more:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [lastVisible, hasMore, loadingMore]);

  const lastItemRef = useCallback(node => {
    if (loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });
    if (node) observer.current.observe(node);
  }, [loadingMore, hasMore, loadMore]);
  // ---------------------------------------------------

  useEffect(() => {
    if (loading) return; 
    if (searchTerm === '' && category === 'All') {
      setFilteredListings(listings);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      try {
        const searchEngine = httpsCallable(functions, 'searchListings');
        const response = await searchEngine({ term: searchTerm, category: category });
        setFilteredListings(response.data.results);
      } catch (error) {
        console.error("Search Failed:", error);
      }
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, category, loading, listings]); 

  const handleSaveSearch = async () => {
    if (!auth.currentUser) return alert("Please log in to save searches!");
    if (!searchTerm && category === "All") return alert("Enter a search term or select a category first.");

    try {
      await addDoc(collection(db, "wishlists"), {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        searchTerm: searchTerm || "Anything",
        category: category,
        createdAt: serverTimestamp()
      });
      alert(`Wishlist Updated! We will email you when new "${searchTerm || category}" gear arrives.`);
    } catch (error) { 
      alert("Failed to save search."); 
    }
  };

  const handleToggleFavorite = async (e, itemId) => {
    e.preventDefault(); 
    if (!auth.currentUser) return alert("Please log in to save gear!");

    const userRef = doc(db, 'users', auth.currentUser.uid);
    const isFavorited = userFavorites.includes(itemId);

    try {
      if (isFavorited) {
        await updateDoc(userRef, { favorites: arrayRemove(itemId) });
        setUserFavorites(prev => prev.filter(id => id !== itemId));
      } else {
        await updateDoc(userRef, { favorites: arrayUnion(itemId) });
        setUserFavorites(prev => [...prev, itemId]);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      alert("Could not update favorites.");
    }
  };

  const handleShare = async (e, item) => {
    e.preventDefault();
    const shareData = {
      title: item.title,
      text: `Check out this ${item.title} on Nundinaria!`,
      url: `${window.location.origin}/item/${item.id}`,
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log("User cancelled share or share failed.");
      }
    } else {
      navigator.clipboard.writeText(shareData.url);
      alert("Link copied to clipboard!");
    }
  };

  // 🚨 OPTIMIZED: The getThumbnail function now aggressively requests the 600x600 extension file.
  const getThumbnail = (item) => {
    let url = item.mainImage || item.imageUrls?.[0] || item.imageUrl;
    if (!url) return 'https://via.placeholder.com/400x300?text=No+Image+Available';

    if (url.includes('firebasestorage.googleapis.com') && !url.includes('_600x600')) {
      url = url.replace('.jpg?alt=media', '_600x600.jpg?alt=media')
               .replace('.jpeg?alt=media', '_600x600.jpeg?alt=media')
               .replace('.png?alt=media', '_600x600.png?alt=media')
               .replace('.webp?alt=media', '_600x600.webp?alt=media');
    }
    
    return url;
  };

  const displayedListings = filteredListings
    .filter(item => conditionFilter === 'All' || item.condition === conditionFilter)
    .filter(item => maxPrice === '' || Number(item.price) <= Number(maxPrice))
    .filter(item => {
      if (searchLocation && item.location) {
        if (!item.location.toLowerCase().includes(searchLocation.toLowerCase())) return false;
      } else if (searchLocation && !item.location) {
        return false;
      }
      if (localPickupOnly && item.shippingAvailable === true) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (a.isFeatured && !b.isFeatured) return -1;
      if (!a.isFeatured && b.isFeatured) return 1;
      if (sortOrder === 'priceAsc') return a.price - b.price;
      if (sortOrder === 'priceDesc') return b.price - a.price;
      return 0; 
    });

  // --- SKELETON LOADERS ---
  if (loading) {
    return (
      <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: isMobile ? '15px 10px' : '40px 20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        <style>{`
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: .4; } }
          .skeleton { animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        `}</style>
        
        <div style={{ maxWidth: '1400px', margin: '0 auto', background: '#1e293b', borderRadius: '24px', padding: isMobile ? '30px 20px' : '50px 60px', marginBottom: '30px', height: '160px', className: 'skeleton' }} />
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '40px', flexDirection: isMobile ? 'column' : 'row' }}>
          <div style={{ width: isMobile ? '100%' : '260px', flexShrink: 0, height: '400px', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', className: 'skeleton' }} />
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '160px' : '280px'}, 1fr))`, gap: isMobile ? '16px' : '24px' }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="skeleton" style={{ backgroundColor: 'white', borderRadius: '20px', border: '1px solid #e2e8f0', height: isMobile ? '340px' : '400px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ height: isMobile ? '180px' : '260px', backgroundColor: '#e2e8f0' }} />
                <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px', flexGrow: 1 }}>
                  <div style={{ height: '14px', width: '40%', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                  <div style={{ height: '24px', width: '80%', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                  <div style={{ marginTop: 'auto', height: '28px', width: '35%', backgroundColor: '#e2e8f0', borderRadius: '4px' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', padding: isMobile ? '15px 10px' : '40px 20px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      
      <Helmet>
        <title>Nundinaria | The Premier Gear Marketplace</title>
        <meta name="description" content="Buy, sell, and trade high-quality used gear safely on Nundinaria. Discover great deals in your local neighborhood or ship nationwide." />
      </Helmet>

      {/* GLOBAL CSS FOR MICRO-INTERACTIONS */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .feed-card {
          transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.4s ease, border-color 0.4s ease;
        }
        .feed-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
          border-color: #cbd5e1;
        }
        .feed-image-container {
          overflow: hidden;
        }
        .feed-image {
          transition: transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }
        .feed-card:hover .feed-image {
          transform: scale(1.08);
        }
        .value-prop-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .value-prop-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 30px -5px rgba(0, 0, 0, 0.08);
        }
      `}</style>

      {globalAlert && (
        <div style={{ 
          backgroundColor: globalAlert.type === 'warning' ? '#fef3c7' : globalAlert.type === 'success' ? '#d1fae5' : '#e0e7ff', 
          color: globalAlert.type === 'warning' ? '#92400e' : globalAlert.type === 'success' ? '#065f46' : '#1e40af', 
          padding: '16px 24px', 
          borderRadius: '12px', 
          maxWidth: '1400px',
          margin: '0 auto 24px auto', 
          fontWeight: '600', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          border: `1px solid ${globalAlert.type === 'warning' ? '#fde68a' : globalAlert.type === 'success' ? '#a7f3d0' : '#bfdbfe'}`
        }}>
          <span style={{ fontSize: '20px' }}>{globalAlert.type === 'warning' ? '⚠️' : globalAlert.type === 'success' ? '✨' : 'ℹ️'}</span>
          <span style={{ fontSize: '15px' }}>{globalAlert.message}</span>
        </div>
      )}

      {/* Upgraded Radial Hero Banner */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'radial-gradient(circle at top right, #1e293b, #020617)', borderRadius: '24px', padding: isMobile ? '30px 20px' : '50px 60px', marginBottom: '30px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '24px' : '0', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)', color: 'white', position: 'relative', overflow: 'hidden' }}>
        
        {/* Subtle decorative glow */}
        <div style={{ position: 'absolute', top: '-50%', right: '-10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(79, 70, 229, 0.4) 0%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h2 style={{ margin: '0 0 12px 0', fontSize: isMobile ? '32px' : '44px', fontWeight: '800', letterSpacing: '-1px' }}>Welcome to Nundinaria</h2>
          <p style={{ margin: 0, fontSize: isMobile ? '16px' : '18px', color: '#cbd5e1', fontWeight: '400', maxWidth: '500px', lineHeight: '1.5' }}>Your neighborhood marketplace. Discover curated tech, hardware, and media.</p>
        </div>
        <Link to="/sell" style={{ textDecoration: 'none', width: isMobile ? '100%' : 'auto', zIndex: 1 }}>
          <button style={{ width: isMobile ? '100%' : 'auto', padding: '16px 36px', backgroundColor: '#ffffff', color: '#0f172a', border: 'none', borderRadius: '50px', fontWeight: '700', fontSize: '16px', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(255, 255, 255, 0.2)', transition: 'all 0.2s ease-in-out' }}
            onMouseOver={e => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(255, 255, 255, 0.3)'; }}
            onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(255, 255, 255, 0.2)'; }}
          >
            Start Selling Today
          </button>
        </Link>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto 40px auto', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '24px' }}>
        <div className="value-prop-card" style={{ backgroundColor: 'white', padding: '30px', borderRadius: '20px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px', backgroundColor: '#f1f5f9', padding: '12px', borderRadius: '50%' }}>💎</div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>Unbeatable Margins</h3>
          <p style={{ margin: 0, fontSize: '15px', color: '#64748b', lineHeight: '1.6' }}>Keep what you earn. We charge a flat, transparent <strong>7% fee</strong> on all successful sales.</p>
        </div>

        <div className="value-prop-card" style={{ backgroundColor: 'white', padding: '30px', borderRadius: '20px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px', backgroundColor: '#f0fdf4', padding: '12px', borderRadius: '50%' }}>🛡️</div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>Verified Community</h3>
          <p style={{ margin: 0, fontSize: '15px', color: '#64748b', lineHeight: '1.6' }}>Trade with confidence. Every user is SMS-verified to guarantee a secure, bot-free environment.</p>
        </div>

        <div className="value-prop-card" style={{ backgroundColor: 'white', padding: '30px', borderRadius: '20px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '16px', backgroundColor: '#eff6ff', padding: '12px', borderRadius: '50%' }}>✈️</div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#0f172a', fontWeight: '700' }}>Instant Fulfillment</h3>
          <p style={{ margin: 0, fontSize: '15px', color: '#64748b', lineHeight: '1.6' }}>Skip the lines. Purchase and print discounted shipping labels instantly from your dashboard.</p>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '40px', flexDirection: isMobile ? 'column' : 'row', alignItems: 'flex-start' }}>
        
        {/* Sidebar */}
        <aside style={{ width: isMobile ? '100%' : '260px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '24px', position: isMobile ? 'static' : 'sticky', top: '24px' }}>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h3 style={{ margin: '0 0 8px 4px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Search & Discover</h3>
            <input 
              type="text" 
              placeholder="Search gear..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', backgroundColor: 'white', fontSize: '15px', color: '#0f172a', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s' }}
              onFocus={e => { e.target.style.borderColor = '#4f46e5'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#cbd5e1'; e.target.style.boxShadow = 'none'; }}
            />
            
            {(searchTerm || category !== "All") && (
              <button 
                onClick={handleSaveSearch} 
                style={{ width: '100%', padding: '12px', marginTop: '8px', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', transition: 'background-color 0.2s' }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#4338ca'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4f46e5'}
              >
                Create Alert
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <h3 style={{ margin: '0 0 8px 4px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Location</h3>
            <input 
              type="text" 
              placeholder="e.g., Chicago, IL" 
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', backgroundColor: 'white', fontSize: '15px', color: '#0f172a', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s', marginBottom: '8px' }}
              onFocus={e => { e.target.style.borderColor = '#4f46e5'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)'; }}
              onBlur={e => { e.target.style.borderColor = '#cbd5e1'; e.target.style.boxShadow = 'none'; }}
            />
            
            {/* Custom iOS Style Toggle for Local Pickup */}
            <div 
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #cbd5e1', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setLocalPickupOnly(!localPickupOnly)}
            >
              <span style={{ fontSize: '14px', color: '#475569', fontWeight: '600' }}>Local Pickup Only</span>
              <div style={{ width: '40px', height: '24px', backgroundColor: localPickupOnly ? '#4f46e5' : '#cbd5e1', borderRadius: '20px', position: 'relative', transition: 'background-color 0.3s ease' }}>
                <div style={{ width: '18px', height: '18px', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '3px', left: localPickupOnly ? '19px' : '3px', transition: 'left 0.3s ease, transform 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
              </div>
            </div>
          </div>

          {!isMobile && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <h3 style={{ margin: '0 0 8px 4px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Refine</h3>
              
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} style={selectStyle}>
                <option value="newest">Newest Arrivals</option>
                <option value="priceAsc">Price: Low to High</option>
                <option value="priceDesc">Price: High to Low</option>
              </select>

              <input type="number" placeholder="Max Price ($)" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid #cbd5e1', backgroundColor: 'white', fontSize: '14px', boxSizing: 'border-box', outline: 'none', marginBottom: '4px' }} />

              <select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)} style={selectStyle}>
                <option value="All">All Conditions</option>
                <option value="New">Brand New</option>
                <option value="Used - Like New">Like New</option>
                <option value="Used - Excellent">Excellent</option>
                <option value="Used - Good">Good</option>
                <option value="Used - Fair">Fair</option>
              </select>
              
              <button 
                onClick={() => { setCategory('All'); setMaxPrice(''); setLocalPickupOnly(false); setSearchTerm(''); setConditionFilter('All'); setSearchLocation(''); setSortOrder('newest'); }}
                style={{ width: '100%', padding: '12px', marginTop: '8px', backgroundColor: 'transparent', border: '1px solid #e2e8f0', borderRadius: '12px', fontWeight: '600', cursor: 'pointer', color: '#ef4444', transition: 'background-color 0.2s' }}
                onMouseOver={e => e.currentTarget.style.backgroundColor = '#fef2f2'}
                onMouseOut={e => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Reset Filters
              </button>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <h3 style={{ margin: '0 0 8px 4px', fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '700' }}>Categories</h3>
            <div style={{ display: isMobile ? 'flex' : 'flex', flexDirection: isMobile ? 'row' : 'column', overflowX: isMobile ? 'auto' : 'visible', gap: '6px', paddingBottom: isMobile ? '10px' : '0' }}>
              <button onClick={() => setCategory('All')} style={catStyle(category === 'All', isMobile)}>All Categories</button>
              {availableCategories.map(cat => (
                <button key={cat} onClick={() => setCategory(cat)} style={catStyle(category === cat, isMobile)}>{cat}</button>
              ))}
            </div>
          </div>
        </aside>

        <main style={{ flex: 1, width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px', paddingBottom: '16px', borderBottom: '1px solid #e2e8f0' }}>
            <h2 style={{ margin: 0, fontSize: isMobile ? '22px' : '28px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.5px' }}>
              {searchLocation ? `Near "${searchLocation}"` : (category === 'All' ? 'Curated Feed' : `${category} Selection`)}
            </h2>
            <span style={{ color: '#64748b', fontSize: '14px', fontWeight: '500' }}>
              Showing {displayedListings.length} items
            </span>
          </div>

          {displayedListings.length === 0 ? (
            <div style={{ padding: '80px 20px', textAlign: 'center', backgroundColor: 'white', borderRadius: '24px', border: '1px dashed #e2e8f0' }}>
              <div style={{ fontSize: '48px', opacity: 0.5, marginBottom: '16px' }}>🔭</div>
              <h3 style={{ color: '#0f172a', margin: '0 0 10px 0', fontSize: '20px', fontWeight: '700' }}>No matches found.</h3>
              <p style={{ color: '#64748b', margin: 0, fontSize: '15px' }}>Try relaxing your filters or exploring a different category.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '160px' : '280px'}, 1fr))`, gap: isMobile ? '16px' : '24px' }}>
                {displayedListings.map((item, index) => {
                  
                  const isLastElement = index === displayedListings.length - 1;
                  // Staggered entrance animation delay
                  const animDelay = `${index * 0.05}s`;

                  return (
                    <Link to={`/item/${item.id}`} key={item.id} ref={isLastElement ? lastItemRef : null} style={{ textDecoration: 'none', color: 'inherit', animation: 'fadeInUp 0.6s ease-out forwards', opacity: 0, animationDelay: animDelay }}>
                      <div className="feed-card" style={{ position: 'relative', backgroundColor: 'white', borderRadius: '20px', overflow: 'hidden', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', height: '100%' }}>
                        
                        <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 10, display: 'flex', gap: '8px' }}>
                          <button 
                            style={{ background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(8px)', border: 'none', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', fontSize: '18px', transition: 'transform 0.2s ease' }}
                            onClick={(e) => handleToggleFavorite(e, item.id)}
                            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            title={userFavorites.includes(item.id) ? "Remove from Saved" : "Save for later"}
                          >
                            {userFavorites.includes(item.id) ? '❤️' : '🤍'}
                          </button>
                          
                          <button 
                            style={{ background: 'rgba(255, 255, 255, 0.85)', backdropFilter: 'blur(8px)', border: 'none', borderRadius: '50%', width: '38px', height: '38px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', fontSize: '16px', transition: 'transform 0.2s ease' }}
                            onClick={(e) => handleShare(e, item)}
                            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.1)'}
                            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            title="Share Item"
                          >
                            ↗️
                          </button>
                        </div>

                        <div className="feed-image-container" style={{ position: 'relative', height: isMobile ? '180px' : '260px', backgroundColor: '#f1f5f9' }}>
                          {/* 🚨 SEO & OPTIMIZATION: Enhanced Alt Tag, Thumbnail Request & Waterfall Fallback */}
                          <img 
                            className="feed-image" 
                            src={getThumbnail(item)} 
                            alt={`${item.condition || 'Used'} ${item.brand ? item.brand + ' ' : ''}${item.title}`} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            onError={(e) => { 
                              // If the fast thumbnail fails (because the item is old), try the original large image!
                              if (e.target.src.includes('_600x600')) {
                                e.target.src = e.target.src.replace('_600x600', '');
                              } else {
                                // If the original image ALSO fails, show the placeholder
                                e.target.onerror = null; 
                                e.target.src = 'https://via.placeholder.com/400x300?text=Image+Unavailable'; 
                              }
                            }}
                          />
                          
                          <div style={{ position: 'absolute', top: '16px', right: '16px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                            {item.isVerified && <div style={{ background: 'rgba(16, 185, 129, 0.9)', backdropFilter: 'blur(4px)', color: 'white', padding: '4px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px' }}>✓ VERIFIED</div>}
                            {item.isFeatured && <div style={{ background: 'rgba(245, 158, 11, 0.9)', backdropFilter: 'blur(4px)', color: 'white', padding: '4px 12px', borderRadius: '50px', fontSize: '11px', fontWeight: '800', letterSpacing: '0.5px' }}>⭐ FEATURED</div>}
                          </div>
                          
                          <div style={{ position: 'absolute', bottom: '16px', right: '16px' }}>
                            {!item.shippingAvailable ? (
                               <div style={{ background: 'rgba(255, 255, 255, 0.9)', backdropFilter: 'blur(4px)', color: '#0f172a', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>🤝 LOCAL ONLY</div>
                            ) : (
                               <div style={{ background: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(4px)', color: 'white', padding: '6px 12px', borderRadius: '8px', fontSize: '11px', fontWeight: '700', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>✈️ SHIPS</div>
                            )}
                          </div>
                        </div>

                        <div style={{ padding: isMobile ? '16px' : '24px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.location || 'United States'}
                            </span>
                            
                            {!isMobile && (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {item.sellerBadges?.includes('Fast Shipper') && (
                                  <span style={{ fontSize: '10px', color: '#ea580c', backgroundColor: '#fff7ed', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }} title="Fast Shipper">
                                    ⚡ FAST
                                  </span>
                                )}
                                {item.sellerBadges?.includes('Hardware Guru') && (
                                  <span style={{ fontSize: '10px', color: '#059669', backgroundColor: '#ecfdf5', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }} title="Hardware Guru">
                                    🧠 GURU
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <h3 style={{ margin: '0 0 16px 0', fontSize: isMobile ? '16px' : '18px', color: '#0f172a', lineHeight: '1.4', fontWeight: '600', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.title}</h3>

                          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: isMobile ? '22px' : '26px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>${Number(item.price).toFixed(2)}</span>
                            
                            {!isMobile && (
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {item.brand && (
                                  <span style={{ fontSize: '11px', color: '#4f46e5', backgroundColor: '#e0e7ff', padding: '4px 8px', borderRadius: '6px', fontWeight: '700' }}>
                                    {item.brand.toUpperCase()}
                                  </span>
                                )}
                                <span style={{ fontSize: '11px', color: '#475569', backgroundColor: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontWeight: '600' }}>
                                  {item.condition}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              
              {loadingMore && (
                 <div style={{ textAlign: 'center', padding: '30px 0', color: '#64748b', fontWeight: '600', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                   <div style={{ width: '16px', height: '16px', border: '3px solid #cbd5e1', borderTopColor: '#4f46e5', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                   Fetching more gear...
                 </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

// Internal Styles
const selectStyle = { 
  width: '100%', 
  padding: '12px 16px', 
  borderRadius: '12px', 
  border: '1px solid #cbd5e1', 
  backgroundColor: 'white', 
  fontSize: '14px', 
  boxSizing: 'border-box', 
  outline: 'none', 
  marginBottom: '8px',
  color: '#0f172a',
  cursor: 'pointer'
};

const catStyle = (active, isMobile) => ({ 
  display: isMobile ? 'inline-block' : 'block', 
  width: isMobile ? 'auto' : '100%', 
  whiteSpace: 'nowrap',
  textAlign: isMobile ? 'center' : 'left', 
  padding: '12px 16px', 
  background: active ? '#0f172a' : 'transparent', 
  border: active ? '1px solid #0f172a' : '1px solid transparent', 
  color: active ? 'white' : '#475569', 
  fontWeight: active ? '600' : '500', 
  borderRadius: '12px', 
  cursor: 'pointer', 
  transition: 'all 0.2s ease'
});